import { useCallback } from 'react';

import { useCanvas } from '../../../core/components/canvas/hooks/useCanvas.js';
import { getCanvasColor } from '../../../core/components/canvas/utils/canvas-color.js';
import { getColorByContrast } from '../../../core/utils/color-contrast.js';
import { getFontDetails } from '../../../core/utils/get-font-details.js';
import { getTextMeasurer } from '../../../core/utils/text-measure.js';
import {
  DEFAULT_LABEL_ALIGN,
  DEFAULT_LABEL_BASELINE,
  INCREASED_FONT_WEIGHT,
  RECT_BORDER_RADIUS,
} from '../../constants.js';
import { useTextSize } from '../../context/TextSizeContext.js';
import { useTruncationMode } from '../../hooks/useTruncationMode.js';
import type { TreeMapRect } from '../../types/treemap.js';
import { type TreeMapLabel, buildLabels } from '../../utils/build-labels.js';

interface TreeMapSquareRendererProps {
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  opacity: number;
  name: string;
  value: string;
  skipDrawing?: boolean;
  withLabel?: boolean;
  withValueOnly?: boolean;
}

type TreeMapRectStyle = {
  opacity: number;
  color: string;
};

export function TreeMapSquareRenderer(props: TreeMapSquareRendererProps) {
  const {
    name,
    value,
    width,
    height,
    opacity,
    color,
    x,
    y,
    withValueOnly = false,
    skipDrawing = false,
    withLabel = false,
  } = props;

  const truncationMode = useTruncationMode();
  const textSize = useTextSize();

  const draw = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      if (skipDrawing) {
        return;
      }

      const treemapRect = { width, height, x, y };
      const treemapRectStyle = { opacity, color };

      drawTreeMapRect(ctx, treemapRect, treemapRectStyle);

      if (!withLabel) {
        return;
      }

      const labelsConfig = {
        name,
        value,
        textSize,
        truncationMode,
        withValueOnly,
      };
      const textMeasurer = getTextMeasurer(ctx, textSize, {
        fontWeight: INCREASED_FONT_WEIGHT,
      });
      const labels = buildLabels(labelsConfig, treemapRect, textMeasurer);

      drawLabels(ctx, labels);
    },
    [
      name,
      value,
      width,
      height,
      opacity,
      color,
      x,
      y,
      withValueOnly,
      skipDrawing,
      withLabel,
      truncationMode,
      textSize,
    ],
  );

  useCanvas('treemap-square', draw);

  return null;
}

TreeMapSquareRenderer['displayName'] = 'TreeMapSquareRenderer';

const drawTreeMapRect = (
  ctx: CanvasRenderingContext2D,
  { x, y, width, height }: TreeMapRect,
  { opacity, color }: TreeMapRectStyle,
) => {
  ctx.moveTo(x, y);
  ctx.beginPath();
  ctx.globalAlpha = opacity;

  ctx.fillStyle = getCanvasColor(color);
  ctx.filter = 'none';
  ctx.roundRect(x, y, width, height, RECT_BORDER_RADIUS);
  ctx.fill();
};

const drawLabels = (ctx: CanvasRenderingContext2D, labels: TreeMapLabel[]) => {
  ctx.textAlign = DEFAULT_LABEL_ALIGN;
  ctx.textBaseline = DEFAULT_LABEL_BASELINE;
  ctx.fillStyle = getColorByContrast(ctx.fillStyle as string);

  labels.forEach((label) => drawLabel(ctx, label));
};

const drawLabel = (
  ctx: CanvasRenderingContext2D,
  { fontConfig, text, position }: TreeMapLabel,
) => {
  ctx.font = getFontDetails(
    fontConfig.fontWeight,
    fontConfig.fontSize,
    fontConfig.lineHeight,
  );
  ctx.fillText(text, position.x, position.y);
};
